export type Locale = 'ko' | 'en';

export interface PublicConfig {
  firebase: Record<string, string>;
  firebaseVapidKey: string;
  firebaseSdkVersion: string;
  webrtcUrl: string;
  appClass: number;
  appVersion: string;
  adsenseClientId: string;
  adsenseBannerSlot: string;
  adsenseRewardedSlot: string;
  googlePlayUrl: string;
  appStoreUrl: string;
  phoneVerificationRequired?: boolean;
  webCountryCode?: string;
}

export interface BootstrapData {
  user: Record<string, any>;
  feed: {
    dmCount?: number;
    point?: number;
    stories?: Array<Record<string, any>>;
    posts?: Array<Record<string, any>>;
  };
  config: PublicConfig;
  csrf: string;
}

const htmlEscape = (value: unknown) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const attr = htmlEscape;

export function resolveLocale(): Locale {
  const query = new URLSearchParams(location.search).get('lang')?.toLowerCase();
  if (query === 'ko' || query === 'en') return query;
  const saved = localStorage.getItem('eddaba.locale')?.toLowerCase();
  if (saved === 'ko' || saved === 'en') return saved;
  return (navigator.language || '').toLowerCase().startsWith('ko') ? 'ko' : 'en';
}

export function configureDocumentLocale(locale: Locale): void {
  document.documentElement.lang = locale;
  document.cookie = `eddaba_locale=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
  const manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifest) manifest.href = locale === 'ko' ? '/manifest-ko.webmanifest' : '/manifest-en.webmanifest';
}

export function preservePartnerCode(): void {
  const query = new URLSearchParams(location.search);
  const raw = String(query.get('partner_code') || '').toUpperCase().trim();
  if (!/^[A-Z0-9_-]{1,64}$/.test(raw)) return;
  document.querySelectorAll<HTMLAnchorElement>('a[href="/login"],a[href$="/login"]').forEach((link) => {
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin) return;
    url.searchParams.set('partner_code', raw);
    link.href = `${url.pathname}${url.search}${url.hash}`;
  });
}

export function loadAdsense(clientId: string): void {
  const id = String(clientId || '').trim();
  if (!id || document.querySelector('script[data-eddaba-adsense]')) return;
  const script = document.createElement('script'); script.async = true; script.crossOrigin = 'anonymous'; script.dataset.eddabaAdsense = '1';
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(id)}`;
  document.head.append(script);
}

export function routeName(pathname = location.pathname): string {
  const p = pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
  const exact: Record<string, string> = {
    'profile/edit': 'profile-edit',
    'profile/settings': 'profile-settings',
    'profile/settings/account': 'account-settings',
    'profile/settings/bookmarks': 'bookmarks',
    'profile/settings/activity': 'activity',
    'profile/settings/point-history': 'point-history',
    'profile/settings/close-friends': 'close-friends',
    'profile/settings/notifications': 'notification-settings',
    'profile/settings/messages': 'message-settings',
    'profile/settings/calls': 'call-settings',
    'profile/settings/privacy': 'privacy-settings',
    'profile/settings/blocked-users': 'blocked-users',
  };
  if (exact[p]) return exact[p];
  const last = p.split('/').filter(Boolean).pop() || 'home';
  if (last === 'my') return 'profile';
  return ['home', 'chat', 'call', 'drone', 'profile', 'search'].includes(last) ? last : 'home';
}

export function extractTemplate(fullHtml: string): { body: string; bodyClass: string; title: string } {
  const doc = new DOMParser().parseFromString(fullHtml, 'text/html');
  // Script tags inserted by innerHTML are inert anyway. Remove executable source tags
  // so the React bootstrap owns script order. Keep JSON configuration scripts.
  doc.body.querySelectorAll('script[src]').forEach((node) => node.remove());
  return {
    body: doc.body.innerHTML,
    bodyClass: doc.body.className,
    title: doc.title || 'Eddaba',
  };
}

export async function fetchPublicConfig(): Promise<PublicConfig> {
  const response = await fetch('/api/public-config', { credentials: 'same-origin', cache: 'no-store' });
  const payload = await response.json();
  if (!response.ok || payload.result !== 'ok') throw new Error(payload.message || 'Unable to load Eddaba configuration.');
  return payload.data as PublicConfig;
}

export async function fetchBootstrap(locale: Locale): Promise<BootstrapData> {
  const response = await fetch(`/api/bootstrap?lang=${encodeURIComponent(locale)}`, {
    credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });
  const payload = await response.json().catch(() => ({}));
  if (response.status === 401) {
    location.replace(`/login${location.search.includes('lang=') ? location.search : ''}`);
    throw new Error('session_expired');
  }
  if (!response.ok || payload.result !== 'ok') throw new Error(payload.message || payload.code || 'Failed to load feed.');
  return payload.data as BootstrapData;
}

export function setCsrf(value: string): void {
  if (!value) return;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-hash"]');
  if (meta) meta.content = value;
  document.querySelectorAll<HTMLInputElement>('input[name="csrf"]').forEach((input) => { input.value = value; });
}

function userAvatar(user: Record<string, any>): string {
  return String(user.newImageUrl || user.imageUrl || user.avatarImageUrl || '');
}

function formatPointBalance(value: unknown, locale: Locale): string {
  const point = Number(value || 0);
  const safePoint = Number.isFinite(point) ? Math.max(0, Math.trunc(point)) : 0;
  const numberLocale = locale === 'ko' ? 'ko-KR' : 'en-US';
  return `${safePoint.toLocaleString(numberLocale)}P`;
}

export function hydrateFeedShell(data: BootstrapData, locale: Locale): void {
  const root = document.querySelector<HTMLElement>('[data-feed-root]');
  if (!root) return;
  const user = data.user || {};
  const feed = data.feed || {};
  const config = data.config || ({} as PublicConfig);
  const userId = Number(user.userId || 0);
  const userName = String(user.nickName || user.userName || `User${userId}`);
  const avatar = userAvatar(user);
  const point = Number(feed.point ?? user.point ?? 0);
  const normalPoint = Number((feed as any).normalPoint ?? (user as any).normalPoint ?? 0);
  const eventPoint = Number((feed as any).eventPoint ?? (user as any).eventPoint ?? 0);
  const totalPoint = (normalPoint > 0 || eventPoint > 0) ? (normalPoint + eventPoint) : point;
  const query = new URLSearchParams(location.search);
  const route = routeName();
  const threadId = route === 'chat' ? Math.max(0, Number(query.get('threadId') || 0)) : 0;
  const survivalRoomId = route === 'chat' && threadId <= 0 ? Math.max(0, Number(query.get('survivalRoomId') || 0)) : 0;
  const chatTab = route === 'chat' && threadId <= 0 && (query.get('tab') === 'survival' || survivalRoomId > 0) ? 'survival' : 'chat';

  Object.assign(root.dataset, {
    presenceUrl: String(config.webrtcUrl || ''),
    currentUserId: String(userId),
    currentUserName: userName,
    currentUserAvatar: avatar,
    currentUserPoint: String(totalPoint),
    phoneVerificationRequired: config.phoneVerificationRequired ? '1' : '0',
    phoneVerified: Number(user.phoneVerified || 0) === 1 ? '1' : '0',
    webCountryCode: String(config.webCountryCode || ''),
    isVip: Number(user.vip || user.isVip || 0) > 0 ? '1' : '0',
    accountUser: JSON.stringify({
      ...user,
      userId,
      nickName: userName,
      avatarImageUrl: avatar,
      point: totalPoint,
      normalPoint,
      eventPoint,
      isVip: Number(user.vip || user.isVip || 0) > 0,
    }),
    appClass: String(config.appClass ?? 0),
    initialRoute: route,
    initialIncomingCall: route === 'call' && query.get('incoming') === '1' ? '1' : '0',
    initialChatTab: chatTab,
    initialChatThreadId: String(threadId),
    initialSurvivalRoomId: String(survivalRoomId),
    firebaseConfig: JSON.stringify(config.firebase || {}),
    firebaseVapidKey: String(config.firebaseVapidKey || ''),
    firebaseSdkVersion: String(config.firebaseSdkVersion || ''),
  });

  setCsrf(data.csrf);

  document.querySelectorAll<HTMLElement>('.point-balance,[data-point-badge],#topbar-my-point-text,#profile-point-val').forEach((el) => {
    el.textContent = formatPointBalance(totalPoint, locale);
  });
  const currentPointVal = document.getElementById('charge-current-point-val');
  if (currentPointVal) currentPointVal.textContent = `${totalPoint.toLocaleString('en-US')} P`;
  const normalPointVal = document.getElementById('charge-normal-point-val');
  if (normalPointVal) normalPointVal.textContent = `${normalPoint.toLocaleString('en-US')} P`;
  const eventPointVal = document.getElementById('charge-event-point-val');
  if (eventPointVal) eventPointVal.textContent = `${eventPoint.toLocaleString('en-US')} P`;

  const unread = Number(feed.dmCount || 0);
  document.querySelectorAll<HTMLElement>('#topbar-unread-badge,[data-chat-unread-badge]').forEach((badge) => {
    badge.textContent = unread > 99 ? '99+' : String(unread);
    badge.hidden = unread <= 0;
  });

  renderStories(feed.stories || [], userName, avatar);
  renderPosts(feed.posts || [], userId, locale);
}

function initialOf(value: unknown): string {
  const text = String(value || '').trim();
  return (Array.from(text)[0] || '?').toUpperCase();
}

function renderStories(stories: Array<Record<string, any>>, myName: string, myAvatar: string): void {
  const rail = document.querySelector<HTMLElement>('.story-rail');
  if (!rail) return;
  const mine = rail.querySelector<HTMLElement>('[data-open-create-post]');
  if (mine) {
    const avatarNode = mine.querySelector<HTMLElement>('.story-avatar.my-story');
    if (avatarNode) avatarNode.innerHTML = myAvatar ? `<img src="${attr(myAvatar)}" alt="">` : `<span>${htmlEscape(initialOf(myName))}</span>`;
  }
  rail.querySelectorAll('[data-story]').forEach((node) => node.remove());
  const fragment = document.createDocumentFragment();
  for (const story of stories) {
    if (story.isMine === true || Number(story.isMine || 0) === 1) continue;
    const name = String(story.userName || story.nickName || '');
    const avatar = String(story.avatarImageUrl || '');
    const button = document.createElement('button');
    button.className = 'story-item'; button.type = 'button'; button.dataset.story = '';
    button.dataset.storyName = name; button.dataset.storyAvatar = avatar; button.dataset.storyInitial = initialOf(name);
    button.innerHTML = `<span class="story-avatar${story.hasNewStory ? ' has-new' : ''}">${avatar ? `<img src="${attr(avatar)}" alt="">` : `<span>${htmlEscape(initialOf(name))}</span>`}</span><span>${htmlEscape(name)}</span>`;
    fragment.append(button);
  }
  rail.append(fragment);
}

function formatCount(value: unknown): string {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, '')}K`;
  return String(n);
}

function parseServerTimestamp(dateInput: unknown): number {
  if (!dateInput) return 0;
  if (dateInput instanceof Date) return dateInput.getTime();
  if (typeof dateInput === 'number') return Number.isFinite(dateInput) ? dateInput : 0;

  const text = String(dateInput).trim();
  if (!text || text === '0000-00-00 00:00:00') return 0;

  const iso = text.replace(' ', 'T');
  const valueWithTimeZone = iso.endsWith('Z') || /[+-]\d{2}(:?\d{2})?$/.test(iso)
    ? iso
    : `${iso}Z`;
  const timestamp = Date.parse(valueWithTimeZone);
  if (Number.isFinite(timestamp) && timestamp > 0) return timestamp;

  const fallbackTimestamp = Date.parse(text);
  return Number.isFinite(fallbackTimestamp) && fallbackTimestamp > 0 ? fallbackTimestamp : 0;
}

function relativeTimeText(amount: number, unit: 'minute'|'hour'|'day'|'month'|'year', locale: Locale): string {
  if (locale === 'ko') {
    const suffix = { minute: '분 전', hour: '시간 전', day: '일 전', month: '개월 전', year: '년 전' }[unit];
    return `${amount}${suffix}`;
  }

  const suffix = { minute: 'm ago', hour: 'h ago', day: 'd ago', month: 'mo ago', year: 'y ago' }[unit];
  return `${amount}${suffix}`;
}

export function localizeRelativeTime(timeText: unknown, createdAt: unknown, locale: Locale, now = Date.now()): string {
  const text = String(timeText || '').trim();
  const normalized = text.toLowerCase().replace(/\s+/g, ' ');

  if (['just_now', 'just now', 'now', 'today'].includes(normalized) || text === '방금 전' || text === '오늘') {
    return locale === 'ko' ? '방금 전' : 'Just now';
  }
  if (normalized === 'yesterday' || text === '어제') return locale === 'ko' ? '어제' : 'Yesterday';

  const match = normalized.match(/^(\d+)\s*(mo|months?|m|minutes?|h|hours?|d|days?|y|years?)\s+ago$/);
  if (match) {
    const amount = Number(match[1]);
    const unit = match[2];
    if (unit === 'mo' || unit.startsWith('month')) return relativeTimeText(amount, 'month', locale);
    if (unit === 'm' || unit.startsWith('minute')) return relativeTimeText(amount, 'minute', locale);
    if (unit === 'h' || unit.startsWith('hour')) return relativeTimeText(amount, 'hour', locale);
    if (unit === 'd' || unit.startsWith('day')) return relativeTimeText(amount, 'day', locale);
    return relativeTimeText(amount, 'year', locale);
  }

  const createdTimestamp = parseServerTimestamp(createdAt);
  if (createdTimestamp > 0) {
    const elapsedMilliseconds = Math.max(0, now - createdTimestamp);
    const elapsedMinutes = Math.floor(elapsedMilliseconds / 60_000);
    const elapsedHours = Math.floor(elapsedMinutes / 60);
    const elapsedDays = Math.floor(elapsedHours / 24);
    const elapsedMonths = Math.floor(elapsedDays / 30);
    const elapsedYears = Math.floor(elapsedDays / 365);

    if (elapsedMinutes < 1) return locale === 'ko' ? '방금 전' : 'Just now';
    if (elapsedMinutes < 60) return relativeTimeText(elapsedMinutes, 'minute', locale);
    if (elapsedHours < 24) return relativeTimeText(elapsedHours, 'hour', locale);
    if (elapsedDays < 30) return relativeTimeText(elapsedDays, 'day', locale);
    if (elapsedDays < 365) return relativeTimeText(Math.max(1, elapsedMonths), 'month', locale);
    return relativeTimeText(Math.max(1, elapsedYears), 'year', locale);
  }

  return text;
}

function mediaType(media: Record<string, any>): 'video'|'image' {
  const value = String(media.mediaType ?? media.type ?? '').toLowerCase();
  return value === 'video' || value === '1' ? 'video' : 'image';
}

function renderPosts(posts: Array<Record<string, any>>, userId: number, locale: Locale): void {
  const list = document.querySelector<HTMLElement>('.post-list');
  if (!list) return;
  list.innerHTML = '';
  const content = list.parentElement;
  content?.querySelectorAll(':scope > .state-panel').forEach((node) => node.remove());

  if (!posts.length) {
    const panel = document.createElement('section'); panel.className = 'state-panel';
    panel.innerHTML = `<svg class="state-icon" aria-hidden="true"><use href="/assets/images/icons.svg#image"></use></svg><h1>${locale === 'ko' ? '아직 게시물이 없습니다' : 'No posts yet'}</h1><p>${locale === 'ko' ? '새 게시물이 등록되면 이곳에서 확인할 수 있습니다.' : 'New posts will appear here.'}</p>`;
    content?.insertBefore(panel, list);
    return;
  }

  for (const post of posts) {
    const id = Number(post.id || post.postId || 0);
    const ownerId = Number(post.userId || 0);
    const isMine = Boolean(post.isMine) || ownerId === userId;
    const name = String(post.userName || post.nickName || '');
    const avatar = String(post.avatarImageUrl || '');
    const liked = Boolean(post.isLiked);
    const bookmarked = Boolean(post.isBookmarked);
    const timeText = localizeRelativeTime(post.timeText, post.createdAt, locale);
    const media = Array.isArray(post.mediaList) ? post.mediaList : [];
    const mediaHtml = media.length ? `<div class="media-carousel" data-carousel data-open-post-detail><div class="media-track">${media.map((m: any) => {
      if (mediaType(m) === 'video') return `<figure class="media-slide"><div class="video-slide-wrap" data-video-wrap><video playsinline preload="metadata" poster="${attr(m.thumbUrl || '')}"><source src="${attr(m.fileUrl || '')}"></video><div class="video-feed-controls" data-video-controls><button class="video-control-btn video-toggle-play-btn" type="button" aria-label="Play/Pause" data-action="toggle-play"><svg class="play-icon" viewBox="0 0 24 24" aria-hidden="true"><polygon points="8,5 19,12 8,19" fill="#ffffff"/></svg><svg class="pause-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1" fill="#ffffff"/><rect x="14" y="4" width="4" height="16" rx="1" fill="#ffffff"/></svg></button><button class="video-control-btn video-toggle-mute-btn" type="button" aria-label="Mute/Unmute" data-action="toggle-mute"><svg class="unmuted-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="#ffffff"/></svg><svg class="muted-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="#ffffff"/></svg></button></div></div></figure>`;
      return `<figure class="media-slide"><img src="${attr(m.fileUrl || '')}" alt="" loading="lazy"></figure>`;
    }).join('')}</div>${media.length > 1 ? `<button class="carousel-nav-btn prev hidden" type="button" data-carousel-prev aria-label="Previous"><svg class="icon" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2.5"/></svg></button><button class="carousel-nav-btn next" type="button" data-carousel-next aria-label="Next"><svg class="icon" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" stroke-width="2.5"/></svg></button><span class="media-counter" data-media-counter>1/${media.length}</span><div class="media-dots" aria-hidden="true">${media.map((_: any, i: number) => `<span class="${i === 0 ? 'active' : ''}"></span>`).join('')}</div>` : ''}</div>` : '';
    const article = document.createElement('article');
    article.className = 'post-card'; article.dataset.postId = String(id); article.dataset.userId = String(ownerId); article.dataset.isMine = isMine ? '1' : '0'; article.dataset.userName = name; article.dataset.avatar = avatar;
    article.innerHTML = `<header class="post-header"><div class="post-author" data-open-profile data-user-id="${ownerId}" style="cursor:pointer"><span class="avatar small${isMine ? ' my-avatar' : ''}">${isMine ? `<span>${locale === 'ko' ? '나' : 'Me'}</span>` : avatar ? `<img src="${attr(avatar)}" alt="">` : `<span>${htmlEscape(initialOf(name))}</span>`}</span><span class="author-copy"><strong>${htmlEscape(name)}</strong>${post.isVerified ? `<span class="verified" title="Verified">✓</span>` : ''}${post.location ? `<small>${htmlEscape(post.location)}</small>` : ''}</span></div><button class="icon-button post-more-btn" type="button" data-post-options title="${locale === 'ko' ? '더보기' : 'More'}"><svg class="icon"><use href="/assets/images/icons.svg#more"></use></svg></button></header>${mediaHtml}<div class="post-body"><div class="post-actions-row"><button class="action-btn${liked ? ' active' : ''}" type="button" data-like data-url="/api/posts/like"><svg class="action-icon" viewBox="0 0 24 24"><use href="/assets/images/icons.svg#${liked ? 'heart-filled' : 'heart'}"></use></svg><span class="action-count" data-like-count>${formatCount(post.likeCount)}</span></button><button class="action-btn" type="button" data-comments-trigger data-comment-count-num="${Number(post.commentCount || 0)}"><svg class="action-icon" viewBox="0 0 24 24"><use href="/assets/images/icons.svg#message-circle"></use></svg><span class="action-count" data-comment-count>${formatCount(post.commentCount)}</span></button><button class="action-btn icon-only" type="button" data-share><svg class="action-icon" viewBox="0 0 24 24"><use href="/assets/images/icons.svg#share"></use></svg></button><div class="action-row-spacer"></div>${!isMine ? `<button class="action-btn icon-only${bookmarked ? ' active' : ''}" type="button" data-bookmark data-url="/api/posts/bookmark"><svg class="action-icon" viewBox="0 0 24 24"><use href="/assets/images/icons.svg#${bookmarked ? 'bookmark-filled' : 'bookmark'}"></use></svg></button>` : ''}</div><div class="post-content-meta">${post.caption ? `<p class="post-caption"><strong class="author-name">${htmlEscape(name)}</strong> <span class="caption-text">${htmlEscape(post.caption)}</span></p>` : ''}<time class="post-time">${htmlEscape(timeText)}</time></div></div>`;
    list.append(article);
  }
}

export async function loadScript(src: string, module = false): Promise<void> {
  if (document.querySelector(`script[data-cf-migrated="${CSS.escape(src)}"]`)) return;
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.dataset.cfMigrated = src; s.async = false; if (module) s.type = 'module';
    s.addEventListener('load', () => resolve(), { once: true });
    s.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
    document.body.append(s);
  });
}

export async function loadLegacyCore(): Promise<void> {
  await loadScript('/assets/js/drone.js');
  await loadScript('/assets/js/app.js');
  await loadScript('/assets/js/call.js');
}

export function configureSignupForm(): void {
  const birth = document.querySelector<HTMLInputElement>('input[name="birth"]');
  if (!birth) return;
  const today = new Date();
  const cutoff = new Date(today.getFullYear() - 19, today.getMonth(), today.getDate());
  const yyyy = cutoff.getFullYear(); const mm = String(cutoff.getMonth()+1).padStart(2,'0'); const dd = String(cutoff.getDate()).padStart(2,'0');
  birth.max = `${yyyy}-${mm}-${dd}`;
  birth.min = '1900-01-01';
}

export function configureLogin(config: PublicConfig): void {
  const root = document.querySelector<HTMLElement>('[data-auth-root]');
  if (root) {
    root.dataset.configured = config.firebase?.apiKey ? 'true' : 'false';
    root.dataset.sdkVersion = config.firebaseSdkVersion || '12.17.1';
    root.dataset.endpoint = '/auth/social';
    root.dataset.sessionEndpoint = '/auth/session';
  }
  const node = document.getElementById('firebase-config');
  if (node) node.textContent = JSON.stringify(config.firebase || {});
}

export function signalFailure(message: string): void {
  console.error('[Cloudflare migration]', message);
  const splash = document.getElementById('app-launch-splash');
  if (splash) splash.setAttribute('aria-hidden', 'true');
  const toast = document.getElementById('app-toast');
  if (toast) { toast.textContent = message; toast.removeAttribute('hidden'); }
  document.documentElement.dataset.eddabaAppReady = '1';
  window.dispatchEvent(new CustomEvent('eddaba:app-ready', { detail: { screen: 'error' } }));
}
